/* ActiveCaptain notes, from the layer cache's bbox feed.
 *
 * ═══ THE SAME LAYER, FED A DIFFERENT WAY ═══
 *
 * The boat reads `signalk-crows-nest` over the SignalK resources API — see
 * `apps/dash/src/lib/notes.svelte.ts`, which stays exactly as it is. That path
 * works with the radio off, which is the whole reason the boat has a plugin
 * doing it, and nothing here should tempt anyone to replace it.
 *
 * This is the answer for a host with no SignalK. It cannot go straight to
 * Garmin, either: the community API sends no CORS headers at all and answers a
 * preflight with a 400, so a browser is not allowed to ask. The layer cache is,
 * and it caches the answer for everyone looking at the same water. See
 * `services/layer-cache/src/feeds/activecaptain.ts`.
 *
 * ═══ EVERY GUARD BELOW WAS PAID FOR ON THE BOAT ═══
 *
 * The span cap, the TTL, the padded box, the moved-far-enough test and above
 * all the error cooldown are ported from the boat's store rather than
 * rewritten, because each of them exists because of something that actually
 * happened. The cooldown in particular: without it an erroring endpoint is
 * re-asked on EVERY sweep — the view re-centres, the sweep fires, the fetch
 * fails — and a broken upstream takes the whole connection budget with it in
 * identical 400s.
 */

import { sources } from '@gennetay/chart-core/sources';
import type { Bbox } from '@gennetay/chart-core/lib/geo';

import type { NoteView } from './index.ts';

/** A structured block of a POI's detail, as the feed emits it.
 *
 * NO HTML, deliberately, and this is the one real difference from the boat.
 * crows-nest renders these blocks into an HTML description because a SignalK
 * note carries a `description` string and a generic client can only show text —
 * which is why the boat needs `sanitize-html.ts` and the app's only `{@html}`.
 * The feed keeps them structured all the way here, so this host renders
 * label/value rows from data it never has to trust as markup. */
export interface NoteSection {
  id: string;
  title: string;
  items: { label: string; value: string | number; kind: string }[];
}

export interface NoteDetail {
  id: string;
  name: string;
  lat: number;
  lon: number;
  icon: string;
  sections: NoteSection[];
}

interface ListReply {
  ok: boolean;
  at: number;
  stale: boolean;
  data: { bbox: Bbox; points: NoteView[] };
}

interface DetailReply {
  ok: boolean;
  at: number;
  stale: boolean;
  data: NoteDetail;
}

/**
 * The widest view worth asking about, and the widest the feed will answer.
 *
 * ═══ WHY THIS IS NOT THE PLACE TO PUT A ZOOM GATE ═══
 *
 * The cost of this layer is all in the box — measured cold against the layer
 * cache, which is what "ActiveCaptain takes a long time to load" is made of:
 *
 *     5°  2008 points  250 kB  3.8 s
 *     4°                       1.8 s
 *     2°   894 points          1.4 s
 *     1°   305 points          1.0 s
 *
 * So tightening this looks like the fix, and it is not, because a SPAN is not a
 * zoom. The opening view is a fixed z9, so how much water it covers depends on
 * how wide the window is: 1.9° at 1440 px, 2.56° at 1920, 3.44° at 2560. Any
 * cap tight enough to save real time greys the layer out at the app's own
 * default view on an ordinary desktop, which is a worse thing than a slow
 * layer — see `tooWide` and the reason it feeds.
 *
 * 5° stays because it is not a preference: it is the number the feed itself
 * enforces, and the two have to agree or every wide pan is a 400. The real fix
 * for the load cost is in `pack.ts` — not fetching for a layer nobody switched
 * on. This remains what it always was, the point past which a view is
 * passage-scale and there is nothing useful to ask for.
 */
const MAX_SPAN_DEG = 5;
/** Notes are written by people about places that do not move. The feed itself
 *  holds an answer for six hours; this only stops the same question being asked
 *  again while the view sits still. */
const TTL_MS = 10 * 60_000;
/** Pads the fetched box past what is on screen, so a small pan does not blank
 *  the layer and immediately re-fetch. */
const MARGIN = 0.5;
/** Back off after a failure. See the header — this one is load-bearing. */
const ERROR_COOLDOWN_MS = 30_000;

export class NotesFeed {
  items = $state<NoteView[]>([]);
  loading = $state(false);
  error = $state<string | null>(null);
  /** True when the answer came from the feed's stale window — real, but old. */
  stale = $state(false);
  /** True while the view is wider than this layer will ask about. Drives the
   *  greyed row and its reason, so a passage view says "zoom in" instead of
   *  drawing empty water. */
  tooWide = $state(false);

  #inFlight = false;
  #at = 0;
  #errorAt = 0;
  #center: { lat: number; lon: number } | null = null;
  #radius = 0;

  get available(): boolean {
    return sources().has('feeds');
  }

  /**
   * Nobody is looking at this layer. Drop what we hold and forget where it came
   * from, so switching back on re-asks for the water actually on screen.
   *
   * The data is not worth keeping against a later toggle: every reader — the
   * search index, the object pick, the layer itself — already refuses to use it
   * while the layer is off, so held items are invisible either way. Keeping
   * them only creates the one visible failure, which is a toggle that paints
   * the last harbour's pins over the harbour you are in now for the second
   * before the next sweep lands. The feed holds its own answer for six hours,
   * so coming back to the same water costs about 110 ms.
   */
  idle(): void {
    if (this.items.length) this.items = [];
    this.#center = null;
  }

  /**
   * The span verdict for a view, decided without asking the network anything.
   *
   * Separate from `sweep` and from `idle` because it is a fact about the VIEW,
   * not about the layer's state, and it has to stay true while the layer is
   * off. `tooWide` is what makes the layer unavailable, and an unavailable
   * layer is not swept — so if this only ran inside `sweep`, the flag could be
   * set once and never cleared, and the row would stay greyed out at every
   * zoom forever. Clearing it in `idle` instead just moves the fault: the layer
   * would become available, sweep, go too wide, and grey out again on a loop
   * once a second.
   */
  observe(b: Bbox | null): void {
    if (b === null) return;
    this.tooWide = Math.max(b.north - b.south, b.east - b.west) > MAX_SPAN_DEG;
  }

  /** Safe to call on every sweep: the staleness and distance checks below skip
   *  the actual fetch until the view has moved far enough to need one. */
  async sweep(b: Bbox | null): Promise<void> {
    if (!this.available || b === null) return;

    const spanLat = b.north - b.south;
    const spanLon = b.east - b.west;
    /* CLEARED, not left stale, above the cap. A passage view holding the pins
     * from the last harbour view is a chart claiming there are anchorages where
     * it has not looked. */
    this.observe(b);
    if (this.tooWide) {
      if (this.items.length) this.items = [];
      return;
    }
    const cLat = (b.north + b.south) / 2;
    const cLon = (b.east + b.west) / 2;
    const radius = Math.max(spanLat, spanLon) / 2;
    const now = Date.now();

    if (this.#inFlight) return;
    if (now - this.#errorAt < ERROR_COOLDOWN_MS) return;

    if (this.#center) {
      const moved = Math.hypot(cLat - this.#center.lat, cLon - this.#center.lon);
      /* Re-fetch once the centre has moved more than half the padded box's
       * radius — short of that the pad below still covers the view. */
      if (moved < this.#radius / 2 && now - this.#at < TTL_MS) return;
    }

    this.#inFlight = true;
    this.loading = true;
    try {
      /* ═══ THE PAD MUST NOT PUSH THE BOX PAST THE CAP ═══
       *
       * The guard above rejects a view whose span exceeds `MAX_SPAN_DEG`, and
       * then this padded the box by `MARGIN` before sending it — so a view at
       * 4.5° passed the check and left as 6.3°, which the feed refuses with
       *
       *     {"ok":false,"error":"bbox spans more than 5°, which is a passage view"}
       *
       * The client and the server were enforcing the same rule on two different
       * boxes. Measured: a wide Salish Sea view produced
       * `bbox=-127.0766,47.5854,-120.7234,52.5518` — 6.35° of longitude — and a
       * 400 on every pan, which the error cooldown then swallowed into "no
       * anchorages here".
       *
       * The pad is trimmed rather than dropped: it exists so a small pan does
       * not re-fetch, and near the cap there is simply less room for it. */
      const room = (span: number) => Math.max(0, (MAX_SPAN_DEG - span) / 2);
      const padLat = Math.min(spanLat * MARGIN, room(spanLat));
      const padLon = Math.min(spanLon * MARGIN, room(spanLon));
      /* Clamped to the world before it goes out. A view near the antimeridian
       * or over a pole pads past ±180/±90, and the feed rejects that with a 400
       * — which would read as "no anchorages here" after the cooldown swallowed
       * it. */
      const w = Math.max(-180, b.west - padLon);
      const s = Math.max(-90, b.south - padLat);
      const e = Math.min(180, b.east + padLon);
      const n = Math.min(90, b.north + padLat);
      /* Bare numbers. SignalK's resources API wants `bbox=[w,s,e,n]` and 400s
       * without the brackets; this API does not, and the difference has bitten
       * once already. */
      const q = `feed/activecaptain.pois?bbox=${w.toFixed(4)},${s.toFixed(4)},${e.toFixed(4)},${n.toFixed(4)}`;
      const r = await sources().fetch('feeds', q, { signal: AbortSignal.timeout(15_000) });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = (await r.json()) as ListReply;

      this.items = j.data?.points ?? [];
      this.stale = j.stale === true;
      this.error = null;
      this.#center = { lat: cLat, lon: cLon };
      this.#radius = radius;
      this.#at = now;
    } catch (e) {
      this.error = e instanceof Error ? e.message : String(e);
      this.#errorAt = Date.now();
    } finally {
      this.#inFlight = false;
      this.loading = false;
    }
  }

  /** The full record for one note. The bbox list carries only name, position
   *  and icon — no sections — so opening a sheet asks for the rest. Returns
   *  null on any failure, so the sheet can say "no details" rather than throw. */
  async fetchNote(id: string): Promise<NoteDetail | null> {
    if (!this.available) return null;
    try {
      const r = await sources().fetch(
        'feeds',
        `feed/activecaptain.poi?id=${encodeURIComponent(id)}`,
        { signal: AbortSignal.timeout(15_000) }
      );
      if (!r.ok) return null;
      const j = (await r.json()) as DetailReply;
      return j.data ?? null;
    } catch {
      return null;
    }
  }
}

export const notesFeed = new NotesFeed();
